// Reimbursement lifecycle, as the member sees it. Shared by ReimbursementSection,
// ReimbursementHistory and the forms.
//
// The status values are a cross-repo contract, duplicated value for value in toothlens-APIs
// (app/utils/reimbursementLifecycle), insurance-backend (app/models/reimbursement/reimbursementStatus.js)
// and insurance-frontend (src/Components/ReimbursementDashboard/status.ts). Change all four together;
// reimbursementStatus.spec.js pins the list.
//
// Every label says "Reimbursement" so members never mistake this for the clinic prescription flow.

export const REIMBURSEMENT_STATUSES = [
    'reimbursement_prescription_review',
    'reimbursement_prescription_reupload_requested',
    'reimbursement_prescription_rejected',
    'reimbursement_treatment_pending',
    'reimbursement_treatment_review',
    'reimbursement_treatment_reupload_requested',
    'reimbursement_treatment_rejected',
    'reimbursement_completed',
];

export const PRESCRIPTION_APPROVED_LABEL = 'Reimbursement Prescription Approved';

// What the member sees for each status. reimbursement_treatment_pending (approved, waiting for the
// member's proof of treatment) reads as "Prescription Approved" here; reviewers still see it as
// "Treatment Pending" on the dashboard.
export const STATUS_LABEL = {
    reimbursement_prescription_review: 'Reimbursement Prescription Review',
    reimbursement_prescription_reupload_requested: 'Reimbursement Prescription Re-upload Requested',
    reimbursement_prescription_rejected: 'Reimbursement Prescription Rejected',
    reimbursement_treatment_pending: PRESCRIPTION_APPROVED_LABEL,
    reimbursement_treatment_review: 'Reimbursement Treatment Review',
    reimbursement_treatment_reupload_requested: 'Reimbursement Treatment Re-upload Requested',
    reimbursement_treatment_rejected: 'Reimbursement Treatment Rejected',
    reimbursement_completed: 'Reimbursement Completed',
};

const STYLE = {
    review: 'bg-blue-50 border-blue-100 text-blue-700',
    action: 'bg-amber-50 border-amber-100 text-amber-700',
    success: 'bg-green-50 border-green-100 text-green-700',
    danger: 'bg-red-50 border-red-100 text-red-700',
    neutral: 'bg-gray-50 border-gray-100 text-gray-700',
};

export const STATUS_STYLE = {
    reimbursement_prescription_review: STYLE.review,
    reimbursement_prescription_reupload_requested: STYLE.action,
    reimbursement_prescription_rejected: STYLE.danger,
    reimbursement_treatment_pending: STYLE.success,
    reimbursement_treatment_review: STYLE.review,
    reimbursement_treatment_reupload_requested: STYLE.action,
    reimbursement_treatment_rejected: STYLE.danger,
    reimbursement_completed: STYLE.success,
};

export const statusLabel = (status) => STATUS_LABEL[status] || 'Reimbursement';
export const statusStyle = (status) => STATUS_STYLE[status] || STYLE.neutral;

export const TERMINAL_STATUSES = [
    'reimbursement_prescription_rejected',
    'reimbursement_treatment_rejected',
    'reimbursement_completed',
];

export const isTerminalStatus = (status) => TERMINAL_STATUSES.includes(status);

// What the member should do next. `action` drives the single button on the status card:
//   reupload        → re-upload the documents the reviewer asked for
//   upload_proof    → upload proof of treatment
//   new             → start a new reimbursement (after a closed one). The section offers this
//                     with its own always-visible button, since a member may start one at any time.
//   null            → nothing to do but wait
export const NEXT_STEP = {
    reimbursement_prescription_review: {
        title: 'We are reviewing your reimbursement prescription',
        body: 'Our team is checking your prescription and photos. This page updates as soon as it has been reviewed.',
        action: null,
    },
    reimbursement_prescription_reupload_requested: {
        title: 'Please re-upload some documents',
        body: 'Our reviewer needs new copies of the documents listed below before your prescription can be approved.',
        action: 'reupload',
        actionLabel: 'Re-upload documents',
    },
    reimbursement_prescription_rejected: {
        title: 'Your reimbursement prescription was not approved',
        body: 'This reimbursement is closed. You can start a new reimbursement request for another treatment.',
        action: 'new',
        actionLabel: 'Start a new reimbursement',
    },
    reimbursement_treatment_pending: {
        title: 'Action required',
        body: 'Your prescription has been approved. Please upload your proof of treatment after completing the treatment to continue your reimbursement.',
        action: 'upload_proof',
        actionLabel: 'Upload Proof of Treatment',
    },
    reimbursement_treatment_review: {
        title: 'Proof of treatment submitted',
        body: 'Your treatment proof has been submitted. Our reimbursement team is reviewing it.',
        action: null,
    },
    reimbursement_treatment_reupload_requested: {
        title: 'Please re-upload some treatment documents',
        body: 'Our reviewer needs new copies of the documents listed below before your reimbursement can be completed.',
        action: 'reupload',
        actionLabel: 'Re-upload documents',
    },
    reimbursement_treatment_rejected: {
        title: 'Your proof of treatment was not approved',
        body: 'This reimbursement is closed. You can start a new reimbursement request for another treatment.',
        action: 'new',
        actionLabel: 'Start a new reimbursement',
    },
    reimbursement_completed: {
        title: 'Reimbursement completed',
        body: 'Your reimbursement has been approved. Our team will pay it out to the account you provided.',
        action: 'new',
        actionLabel: 'Start a new reimbursement',
    },
};

export const nextStep = (status) => NEXT_STEP[status] || null;

// The line shown while the member has nothing to do.
export const waitingMessage = (status) => (status === 'reimbursement_treatment_review'
    ? "No action needed from you right now. We'll update you once the treatment review is complete."
    : 'No action needed from you right now.');

// Which Reimbursements get a full status card. Every open one does, since each may need the
// member's action and a member may have several. The latest one keeps its card after it closes,
// so its outcome and reviewer note stay in view.
export const reimbursementCards = (open = [], latest = null) => (
    latest && !open.some((r) => r._id === latest._id) ? [...open, latest] : open
);

// The progress tracker on the status card.
export const STEPS = [
    'Reimbursement Prescription Review',
    PRESCRIPTION_APPROVED_LABEL,
    'Reimbursement Proof of Treatment',
    'Reimbursement Treatment Review',
    'Reimbursement Completed',
];

// For each status: how far along the tracker it is, and whether the current step failed.
const PROGRESS = {
    reimbursement_prescription_review: { current: 0 },
    reimbursement_prescription_reupload_requested: { current: 0, attention: true },
    reimbursement_prescription_rejected: { current: 0, failed: true },
    reimbursement_treatment_pending: { current: 2 },
    reimbursement_treatment_review: { current: 3 },
    reimbursement_treatment_reupload_requested: { current: 3, attention: true },
    reimbursement_treatment_rejected: { current: 3, failed: true },
    reimbursement_completed: { current: 4, done: true },
};

// [{ label, state: 'done' | 'current' | 'attention' | 'failed' | 'upcoming' }]
export const trackerSteps = (status) => {
    const progress = PROGRESS[status] || { current: 0 };
    return STEPS.map((label, index) => {
        let state = 'upcoming';
        if (index < progress.current || (progress.done && index === progress.current)) state = 'done';
        else if (index === progress.current) state = progress.failed ? 'failed' : progress.attention ? 'attention' : 'current';
        return { label, state };
    });
};

// Model field → the upload endpoint's `field` name (pre_op_xray_urls is two upload slots).
export const UPLOAD_FIELD = {
    reimbursement_approval_file_url: 'prescription_file',
    upper_occlusal_photo_url: 'upper_occlusal_photo',
    lower_occlusal_photo_url: 'lower_occlusal_photo',
    problem_area_photo_url: 'problem_area_photo',
    additional_photo_url: 'additional_photo',
    pre_op_xray_urls: ['pre_op_xray_1', 'pre_op_xray_2'],
    post_treatment_photo_url: 'post_treatment_photo',
    invoice_receipt_url: 'invoice_receipt',
};

export const FIELD_LABEL = {
    reimbursement_approval_file_url: 'Prescription',
    upper_occlusal_photo_url: 'Upper Occlusal photo',
    lower_occlusal_photo_url: 'Lower Occlusal photo',
    problem_area_photo_url: 'Problem Area photo',
    additional_photo_url: 'Additional photo',
    pre_op_xray_urls: 'Pre-Op X-rays',
    post_treatment_photo_url: 'Post-treatment photo',
    invoice_receipt_url: 'Invoice / receipt',
};

export const fieldLabel = (field) => FIELD_LABEL[field] || field;

// Proof-of-treatment file field → where the API keeps the name the member picked (display only).
export const FILE_NAME_FIELD = {
    post_treatment_photo_url: 'post_treatment_photo_name',
    invoice_receipt_url: 'invoice_receipt_name',
};

// Upload fields that only accept images (the server rejects PDFs for these).
const DOCUMENT_UPLOAD_FIELDS = ['prescription_file', 'invoice_receipt'];
export const acceptFor = (uploadField) => (DOCUMENT_UPLOAD_FIELDS.includes(uploadField) ? 'image/*,.pdf' : 'image/*');

export const HISTORY_LABEL = {
    submit_prescription: 'Reimbursement requested',
    reupload_prescription: 'You re-uploaded prescription documents',
    approve_prescription: PRESCRIPTION_APPROVED_LABEL,
    reject_prescription: STATUS_LABEL.reimbursement_prescription_rejected,
    request_prescription_reupload: STATUS_LABEL.reimbursement_prescription_reupload_requested,
    submit_treatment_proof: 'You uploaded proof of treatment',
    reupload_treatment: 'You re-uploaded treatment documents',
    complete: STATUS_LABEL.reimbursement_completed,
    reject_treatment: STATUS_LABEL.reimbursement_treatment_rejected,
    request_treatment_reupload: STATUS_LABEL.reimbursement_treatment_reupload_requested,
    backfill: 'Moved to the new reimbursement tracker',
    update_procedures: 'Treatment plan updated by ToothLens',
    decide_procedures: 'Procedure decisions updated',
};

export const historyLabel = (action) => HISTORY_LABEL[action] || 'Updated';

// --- Recommended procedures --------------------------------------------------------------------
// The treatment plan a ToothLens reviewer transcribed from the member's prescription, each
// procedure approved (for an amount) or not approved (with a reason) one by one.

export const formatInr = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export const procedureStatus = (procedure) => {
    if (procedure.decision === 'approved') {
        return { label: `Approved — ${formatInr(procedure.approved_amount)}`, style: STYLE.success };
    }
    if (procedure.decision === 'rejected') return { label: 'Not approved', style: STYLE.danger };
    return { label: 'Under review', style: STYLE.review };
};

export const approvedProcedures = (procedures = []) => procedures.filter((p) => p.decision === 'approved');

// Whether the status card lists the procedures. A prescription rejected because every procedure was
// rejected lists them with their reasons; one rejected by hand does not (the API sends its procedures
// undecided, and the rejection note explains instead).
export const showsProcedures = (reimbursement) => {
    const procedures = reimbursement?.procedures || [];
    if (!procedures.length) return false;
    if (reimbursement.reimbursement_status !== 'reimbursement_prescription_rejected') return true;
    return procedures.some((p) => p.decision !== 'pending');
};

// The server sends approved_total; computed here too for older responses.
export const approvedTotalOf = (reimbursement) => (typeof reimbursement?.approved_total === 'number'
    ? reimbursement.approved_total
    : approvedProcedures(reimbursement?.procedures).reduce((sum, p) => sum + (Number(p.approved_amount) || 0), 0));

// "Tooth 36", but "Full Mouth" / "Quadrant 2" as they are.
export const toothLabel = (tooth) => (/^\d+$/.test(String(tooth)) ? `Tooth ${tooth}` : String(tooth || ''));

// --- Proof of treatment ------------------------------------------------------------------------
// Once the prescription is approved, the status card asks for the invoice/receipt first (it is
// uploaded straight away), then the proof form collects the rest.

// The card accepts fewer types than the server (which also takes doc/xls for the invoice).
// maxMb mirrors the server's MAX_IMAGE_SIZE (toothlens-APIs app/utils/constants).
export const PROOF_FILE = {
    extensions: ['pdf', 'jpg', 'jpeg', 'png'],
    mimeTypes: ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'],
    maxMb: 50,
    accept: '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png',
    hint: 'PDF, JPG, JPEG or PNG · up to 50 MB',
};

// Why a picked file cannot be used as proof, or null when it can.
export const proofFileProblem = (file) => {
    if (!file) return 'Please choose a file.';
    const extension = String(file.name || '').split('.').pop().toLowerCase();
    if (!PROOF_FILE.extensions.includes(extension) || (file.type && !PROOF_FILE.mimeTypes.includes(file.type))) {
        return 'Please upload a PDF, JPG, JPEG or PNG file.';
    }
    if (file.size > PROOF_FILE.maxMb * 1024 * 1024) return `This file is larger than ${PROOF_FILE.maxMb} MB.`;
    if (!file.size) return 'This file is empty.';
    return null;
};

export const formatFileSize = (bytes) => {
    const size = Number(bytes) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

// Reimbursements approved before procedure-wise approval have no plan, so no amount to quote.
export const proofRequiredMessage = (reimbursement) => {
    const total = approvedTotalOf(reimbursement);
    const approved = total > 0
        ? `Your prescription has been approved for reimbursement of ${formatInr(total)}.`
        : 'Your prescription has been approved for reimbursement.';
    return `${approved} After completing the treatment, upload your treatment proof to continue your reimbursement.`;
};

// The documents of a submitted proof of treatment, with the name the member picked (older proofs
// have none, so the document type stands in) and when it was uploaded. Only the member writes a
// proof, so its last update is the last upload.
export const submittedProofDocuments = (proof) => {
    if (!proof) return [];
    const at = proof.updatedAt || proof.createdAt || null;
    return ['invoice_receipt_url', 'post_treatment_photo_url']
        .filter((field) => proof[field])
        .map((field) => ({ field, label: fieldLabel(field), name: proof[FILE_NAME_FIELD[field]] || fieldLabel(field), at }));
};
